/**
 * Tax Authority Configuration Types
 */

export interface SiiEmisor {
  rut: string;
  razonSocial: string;
  giro: string;
  codigoActividad: number;
  direccion: string;
  comuna: string;
  ciudad: string;
  resolutionNumber: number;
  resolutionDate: string;
  /**
   * RUT of the certificate owner (representative).
   * Required when using a personal certificate to send documents
   * on behalf of the company.
   */
  rutRepresentante?: string;
}

export interface CertificationStatus {
  status: string;
  startedAt?: string;
  approvedAt?: string;
  attentionNumber?: string;
}

export interface TaxAuthorityConfig {
  id: string;
  isEnabled: boolean;
  environment: "certification" | "production";
  emisor: SiiEmisor | null;
  hasCertificate: boolean;
  hasSiiWebCredentials: boolean;
  certificateExpiry?: string;
  authorizationReference: string | null;
  authorizationDate: string | null;
  certification?: CertificationStatus;
  accountingConfig?: AccountingConfig;
}

export interface FolioAuthorization {
  id: string;
  documentType: string;
  documentTypeCode: string;
  folioStart: number;
  folioEnd: number;
  folioCurrent: number;
  foliosRemaining: number;
  authorizedAt: string;
  expiresAt: string | null;
  isActive: boolean;
}

export interface VerifyConnectionResult {
  success: boolean;
  message: string;
  environment: string;
}

/**
 * Document type code to human readable name
 */
export const DTE_TYPE_NAMES: Record<string, string> = {
  "33": "Factura Electrónica",
  "34": "Factura Exenta Electrónica",
  "52": "Guía de Despacho Electrónica",
  "56": "Nota de Débito Electrónica",
  "61": "Nota de Crédito Electrónica",
};

/**
 * Accounting configuration for tax document journal entries.
 */
export interface AccountingConfig {
  /** Whether automatic journal entry creation is enabled */
  enabled?: boolean;
  /** Whether to auto-post journal entries (true) or create as draft (false) */
  autoPost?: boolean;
  /** Account ID for IVA Crédito Fiscal (Input VAT) */
  inputVatAccountId?: string;
  /** Account ID for IVA Débito Fiscal (Output VAT) */
  outputVatAccountId?: string;
  /** Account ID for Accounts Payable */
  accountsPayableAccountId?: string;
  /** Account ID for Accounts Receivable */
  accountsReceivableAccountId?: string;
  /** Default expense account for received invoices */
  defaultExpenseAccountId?: string;
  /** Default revenue account for issued invoices */
  defaultRevenueAccountId?: string;
}
